/* eslint-disable no-debugger */
import { useState, useEffect } from 'react';
import useForm from '@usas/ui-web/utils/UseForm';
import validate from "./CompetitionDetailKendoReportValidate";
import useReportPeriodFlatData from '@usas/ui-web/state/reportPeriodFlat/UseReportPeriodFlatData';
import Constants from '@usas/ui-web/utils/Constants';
import ToIntIfInt from '@usas/ui-web/utils/ToIntIfInt';

import useOrgRoleData from '@usas/ui-web/state/orgRole/UseOrgRoleData';
import useOrgUnitData from '../../state/orgUnit/UseOrgUnitData';


// Define the columns configuration for this report
const columns = [
  { width: "90px", id: "LscCode", field: "lscCode", title: "LSC" },
  { width: "110px", id: "ClubCode", field: "clubCode", title: "Club Code" },
  { width: "260px", id: "ClubName", field: "clubName", title: "Club Name" },
  { width: "150px", id: "MemberId", field: "memberId", title: "Member ID" },
  { width: "120px", id: "FirstName", field: "firstName", title: "First Name" },
  { width: "120px", id: "MiddleName", field: "middleName", title: "Middle Name" },
  { width: "120px", id: "LastName", field: "lastName", title: "Last Name" },
  { width: "100px", id: "EventGender", field: "eventGender", title: "Competition Category" },
  { width: "100px", id: "SwimmerAge", field: "swimmerAge", title: "Age" },
  { width: "90px", id: "SeasonYear", field: "seasonYear", title: "Season Year" },
  { width: "150px", id: "MemberType", field: "memberType", title: "Member Type" },
  { width: "130px", id: "TypeStandardShortType", field: "timeStandardShortType", title: "Highest Standard" },
  { width: "90px", id: "MeetsAttended", field: "meetsAttended", title: "Meets Attended" },
  { width: "90px", id: "Splashes", field: "splashes", title: "Splashes" },
  { width: "90px", id: "ImprovementRate", field: "improvementRate", title: "Improvement Rate", format: "{0:p1}" },
];

const excelColumns = [
  { id: "LscCode", field: "lscCode", title: "LSC" },
  { id: "ClubCode", field: "clubCode", title: "Club Code" },
  { id: "ClubName", field: "clubName", title: "Club Name" },
  { id: "MemberId", field: "memberId", title: "Member ID" },  
  { id: "FirstName", field: "firstName", title: "First Name" },
  { id: "MiddleName", field: "middleName", title: "Middle Name" },
  { id: "LastName", field: "lastName", title: "Last Name" },
  { id: "EventGender", field: "eventGender", title: "Competition Category" },
  { id: "SwimmerAge", field: "swimmerAge", title: "Age" },
  { id: "SeasonYear", field: "seasonYear", title: "Season Year" },
  { id: "MemberType", field: "memberType", title: "Member Type" },
  { id: "TypeStandardShortType", field: "timeStandardShortType", title: "Highest Standard" },
  { id: "MeetsAttended", field: "meetsAttended", title: "Meets Attended" },
  { id: "Splashes", field: "splashes", title: "Splashes" },
  { id: "ImprovementRate", field: "improvementRate", title: "Improvement Rate" },
  { id: "EventDetail_EventCode", field: "eventDetail_eventCode", title: "Event" },
  { id: "EventDetail_EventHighestStandard", field: "eventDetail_eventHighestStandard", title: "Highest Standard" },
  { id: "EventDetail_EventSplashes", field: "eventDetail_eventSplashes", title: "Splashes" },
  { id: "EventDetail_EventProgression", field: "eventDetail_eventProgression", title: "Progression" }
];

const REPORT_INFO_ID = 285;

const useCompetitionDetailKendoReport = () => {
  const { reportPeriodFlatState, getReportPeriodsFlatByOrganizationId } = useReportPeriodFlatData();
  const [ seasonYearState, setSeasonYearState ] = useState({ isLoading: true, isLoaded: false, options: [] });
  const [ memberTypeState, setMemberTypeState ] = useState({ isLoading: true, isLoaded: false, options: [] });
  const [ modalState, setModalState ] = useState(getInitialModalState());

  const { orgUnitState } = useOrgUnitData(); 
  const { orgRoleState } = useOrgRoleData();

  const {
    formState,
    errorState,
    handleSubmit,
    onFormValueChanged,
    onValueTextPairChanged,
    setFormState,
    setIsDirty,
    updateFormState
  } = useForm(getInitialFormState(), submitFormCallback, validate);

  function submitFormCallback() {
    setModalState({ ...modalState, displayPopUp: true });
  }

  const onRunReportClicked = (e) => {
    handleSubmit(e);
  }

  const onClearForm = () => {
    setFormState(getInitialFormState());
    setIsDirty(true);
  }

  const onModalCancelClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setIsDirty(true);
    setModalState({ ...modalState, displayPopUp: false });
  };

  function getInitialModalState() {
    return {
      displayPopUp: false,
      currentPrimaryInfo: '',
      modalTitle: 'Report Viewer'
    };
  }

  function getInitialFormState() {
    return {
      orgUnitId: '',
      seasonId: -1,
      seasonYear: -1,
      minAge: 1,
      maxAge: 99,
      genders: [{ id: 1, name: 'Female' }, { id: 2, name: 'Male' }],
      clubs: [],
      lscs: [],
      zones: [],
      memberTypes: [],
      exportDetailData: false,
      activeRoster: false
    };
  }

  useEffect(() => {
    if (reportPeriodFlatState.isLoading === false && reportPeriodFlatState.isLoaded === false) {
      getReportPeriodsFlatByOrganizationId(1);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (reportPeriodFlatState.isLoaded === true) {
      const years = [];

      reportPeriodFlatState.dataAsOptionsWithNames.map(rp => {
        if (rp.name.substring(5, 6) === '(') {
          years.unshift({ id: rp.id, name: rp.name.substring(0, 4) });
        }
      });
      years.unshift({ id: -1, name: '--' });
      setSeasonYearState({ isLoading: false, isLoaded: true, options: years });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportPeriodFlatState]);

  useEffect(() => {
    if (orgRoleState.isArrayLoaded === true && formState.memberTypes.length > 0) {
      const memtypes = [];

      formState.memberTypes.map(ft => {
        const found = orgRoleState.arrayData.find(mt => mt.orgRoleId === ft.id);
        if (found) {
          memtypes.push({ id: found.orgRoleId, name: found.orgRoleName });
        }
      });

      setMemberTypeState({ isLoading: false, isLoaded: true, options: memtypes });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgRoleState, formState.memberTypes]);

  function getClubFileterIds() {
    if (orgUnitState.arrayData.find(ou => ou.orgUnitId === formState.orgUnitId)?.orgLevelId === 2) { 
      return formState.orgUnitId.toString();
    } else  {
      return formState.lscs.map(c => c.id).join(',')
    } 
  }

  return {
    formState,
    errorState,
    updateFormState,
    reportParameters: {
      OrgUnitId_Int: formState.orgUnitId === "" ? null : formState.orgUnitId,
      SeasonYear_Int: formState.seasonYear === Constants.DEFAULT_ID ? null : parseInt(formState.seasonYear, 10), 
      MinAge_Int: ToIntIfInt(formState.minAge), 
      MaxAge_Int: ToIntIfInt(formState.maxAge), 
      Genders: formState.genders.map(gender => gender.id).join(','), 
      Clubs: formState.clubs.map(club => club.id).join(',') || null, 
      LSCs: formState.lscs.map(lsc => lsc.id).join(',') || null, 
      Zones: formState.zones.map(zone => zone.id).join(',').length === 0 ? null : formState.zones.map(zone => zone.id).join(','), 
      MemberTypes: memberTypeState.options.map(mt => mt.name).join(',') || null, //formState.memberTypes.map(mt => mt.id).join(',') || null,
      ActiveRoster_Int: formState.activeRoster === true ? 1 : 0
    },
    parameters: formState,
    columns: columns,
    modalState,
    orgUnitState,
    orgRoleState,
    onModalCancelClicked,
    handleSubmit,
    onFormValueChanged,
    onValueTextPairChanged,
    onRunReportClicked,
    onClearForm,
    seasonYearState,
    excelColumns,
    clubFilterIds: getClubFileterIds(),
    REPORT_INFO_ID: REPORT_INFO_ID
  };
};

export default useCompetitionDetailKendoReport;